import {useCallback, useState} from 'react';
import styled, {css} from 'styled-components';

import {scToTwColour} from '../../theme';

import {TableHeading2, StyledHeading} from './TableHeading';

// sortBy && onColSort -> controlled state
// !sortBy && !onColSort -> uncontrolled
// !sortBy && onColSort -> uncontrolled + side effect
// sortBy && !onColSort -> uncontrolled + default sort
export interface TableProps2<T extends {id: string}> {
  items: T[];
  headings: HeadingParams2<T>[];
  sortBy?: TableSortBy2 | null;
  onColSort?: (sort: TableSortBy2 | null) => void;
  onRowClick?: (
    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    rowItem: T,
    idx: number
  ) => void;
  before?: React.ReactNode;
  after?: React.ReactNode;
}
export function Table2<T extends {id: string}>({
  headings,
  items,
  sortBy,
  onColSort,
  ...props
}: TableProps2<T>): React.JSX.Element {
  const [__sortBy, __setSortBy] = useState<TableSortBy2 | null>(sortBy || null);

  const isControlled = typeof sortBy !== 'undefined' && !!onColSort;
  const _sortBy = isControlled ? sortBy : __sortBy;

  const rows = items.map((item, i) => (
    <tr
      key={item.id}
      tabIndex={props.onRowClick ? 0 : -1}
      role={props.onRowClick ? 'button' : ''}
      onMouseDown={(e) => e.shiftKey && e.preventDefault()}
      onClick={(e) => props.onRowClick?.(e, item, i)}
    >
      {headings.map(({prop, renderStrategy}) => (
        <td key={prop}>
          {renderStrategy
            ? renderStrategy(item, i)
            : // @ts-expect-error replacing soon
              item[prop]}
        </td>
      ))}
    </tr>
  ));

  const hasSetWidthColumns = headings.some((h) => !!h.width);

  const handleSort = useCallback(
    (sort: TableSortBy2 | null) => {
      if (isControlled) {
        onColSort(sort);
      } else {
        __setSortBy(sort);
        onColSort?.(sort);
      }
    },
    [isControlled, onColSort]
  );

  return (
    <Container>
      <StyledTable $hasSetWidthColumns={hasSetWidthColumns}>
        <thead>
          <tr>
            {headings.map((heading) => (
              <TableHeading2
                key={heading.prop}
                heading={heading}
                sortBy={_sortBy}
                handleSort={handleSort}
              >
                {heading.label}
              </TableHeading2>
            ))}
          </tr>
        </thead>

        <StyledTableBody
          $isClickable={!!props.onRowClick}
          $hasSetWidthColumns={hasSetWidthColumns}
        >
          {props.before || props.after || rows.length ? (
            <>
              {props.before}
              {rows}
              {props.after}
            </>
          ) : (
            <EmptyRow>
              <td colSpan={headings.length}>There&apos;s nothing here yet.</td>
            </EmptyRow>
          )}
        </StyledTableBody>
      </StyledTable>
    </Container>
  );
}

const Container = styled.div`
  overflow-x: scroll;

  ${({theme}) => theme.media.lg`
    overflow-x: unset;
  `}
`;
const StyledTable = styled.table<{$hasSetWidthColumns: boolean}>`
  table-layout: ${({$hasSetWidthColumns}) =>
    $hasSetWidthColumns ? 'fixed' : 'auto'};
  width: 100%;
  min-width: 1500px;
  border: none;
  text-align: left;
  border-collapse: collapse;
  white-space: nowrap;
  text-overflow: ellipsis;

  td,
  ${StyledHeading} {
    padding: 0.6rem 0.4rem;
    font-size: 0.9rem;
  }
  ${StyledHeading} button:not(role='checkbox') {
    margin: -0.6rem -0.4rem;
    padding: 0.6rem 0.4rem;
  }

  ${({theme}) => theme.media.lg`
    min-width: unset;
  `}
`;
const StyledTableBody = styled.tbody<{
  $isClickable: boolean;
  $hasSetWidthColumns: boolean;
}>`
  tr {
    border-top: 1px solid ${scToTwColour('theme.colours.slate6')};

    &:hover {
      cursor: ${({$isClickable}) => ($isClickable ? 'pointer' : 'unset')};
      background: ${scToTwColour('theme.colours.slate4')};
    }

    td {
      line-height: 1.4em;

      ${({$hasSetWidthColumns}) =>
        $hasSetWidthColumns &&
        css`
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        `}
    }
  }
`;
const EmptyRow = styled.tr`
  td {
    text-align: center;
  }
`;

export type HeadingParams2<T extends {id: string}> = {
  label: React.ReactNode;
  prop: string;
  width?: string;
  sortable?: boolean;
  renderStrategy?: (value: T, idx: number) => React.ReactNode;
};
export type TableSortBy2 = {
  prop: string;
  isAsc: boolean;
};
